/** @type {import('node_modules/vue-router/types/index').RouteConfig[]} */
import i18n from '@/i18n/i18n'
import store from '@/store/store.js'
import { faList, faPlus } from '@fortawesome/free-solid-svg-icons'
import { isEmailVerified } from '@/router/guards/support-email-verified.js'
import { betaGuard } from '@/router/guards/beta-guard.js'
import SupportAnalytics from '@/app/users/analytics/support.analytics.js'
export const SUPPORT_ROUTE = 'support.tickets'
export const SUPPORT_NEW_TICKET_ROUTE = 'support.newTicket'
export const SUPPORT_TICKET_DETAILS_ROUTE = 'support.ticketDetails'
export const SUPPORT_TICKET_LIST = 'support.ticketList'
export const SUPPORT_SHIPMENTS_TICKET_LIST = 'support.shipments.ticketList'
export const SUPPORT_PLATFORM_TICKET_LIST = 'support.platform.ticketList'
export const SUPPORT_TICKET_DRAFT_LIST = 'support.ticketDraftList'
export const SUPPORT_REFUND_CLAIMS = 'support.refundClaims'
export const SUPPORT_CLAIM = 'support.claim'
export const NEW_SUPPORT_DESIGN_BETA_FLAG = 'new_support_design'
const SUPPORT_AGENT_LOGIN = 'support.agent-login'

const btnCreateTicket = {
  id: 'create-ticket',
  title: () => i18n.t('New ticket'),
  name: SUPPORT_NEW_TICKET_ROUTE,
  icon: faPlus,
  mode: 'primary',
  customClick: () => {
    SupportAnalytics.goToNewTicket()
  },
}

const btnHelpCenter = {
  id: 'open-help-center',
  title: () => i18n.t('Help center'),
  name: 'https://support.sendcloud.com',
  newTab: true,
  mode: 'secondary',
}

const btnListTickets = {
  id: 'support-ticket-list',
  title: () => i18n.t('View tickets'),
  name: SUPPORT_ROUTE,
  icon: faList,
  mode: 'primary',
}

const btnAgentLogin = {
  id: 'agent-login',
  name: SUPPORT_AGENT_LOGIN,
  title: () => i18n.t('Agent login'),
  mode: 'secondary',
}

function getButtons() {
  const newDesignBetaFlag = store.getters.hasBetaFeature(NEW_SUPPORT_DESIGN_BETA_FLAG)
  if (newDesignBetaFlag) {
    return [btnHelpCenter, btnAgentLogin]
  } else {
    return [btnCreateTicket, btnHelpCenter, btnAgentLogin]
  }
}

function isNewDesign() {
  return Boolean(store.getters.user?.enabled_beta_features?.includes(NEW_SUPPORT_DESIGN_BETA_FLAG))
}

export default [
  {
    name: SUPPORT_ROUTE,
    path: '/support',
    component: () => import('@/app/support/views/SupportNav.vue'),
    redirect: () => {
      if (isNewDesign()) {
        return { name: SUPPORT_SHIPMENTS_TICKET_LIST }
      } else {
        return { name: SUPPORT_TICKET_LIST }
      }
    },
    meta: {
      pageTitle: () => i18n.t('Support'),
      permissions: ['manage_tickets'],
      buttons: () => getButtons(),
      showUserId: true,
    },
    children: [
      {
        name: SUPPORT_TICKET_LIST,
        path: '/support/tickets/:action(openchat)?',
        component: () => import('@/app/support/components/AppSupportTicketList.vue'),
        meta: {
          pageTitle: () => i18n.t('Support'),
          permissions: ['manage_tickets'],
        },
      },
      {
        name: SUPPORT_SHIPMENTS_TICKET_LIST,
        path: '/support/tickets/shipments',
        beforeEnter: betaGuard(NEW_SUPPORT_DESIGN_BETA_FLAG, SUPPORT_TICKET_LIST),
        component: () => import('@/app/support/components/SupportTicketListShipments.vue'),
        meta: {
          pageTitle: () => i18n.t('Support'),
          permissions: ['manage_tickets'],
        },
      },
      {
        name: SUPPORT_PLATFORM_TICKET_LIST,
        path: '/support/tickets/platform',
        beforeEnter: betaGuard(NEW_SUPPORT_DESIGN_BETA_FLAG, SUPPORT_TICKET_LIST),
        component: () => import('@/app/support/components/SupportTicketListPlatform.vue'),
        meta: {
          pageTitle: () => i18n.t('Support'),
          permissions: ['manage_tickets'],
        },
      },
      {
        name: SUPPORT_TICKET_DRAFT_LIST,
        path: '/support/tickets/drafts',
        component: () => import('@/app/support/components/AppSupportTicketDraftList.vue'),
        meta: {
          pageTitle: () => i18n.t('Support'),
          permissions: ['manage_tickets'],
        },
      },
    ],
  },
  {
    name: SUPPORT_NEW_TICKET_ROUTE,
    path: '/support/new-ticket',
    beforeEnter: isEmailVerified,
    component: () => import('@/app/support/new-ticket-dynamic/views/SupportNewTicketForm.vue'),
    meta: {
      pageTitle: () => i18n.t('Support'),
      permissions: ['manage_tickets'],
      buttons: [btnListTickets, btnHelpCenter],
      showUserId: true,
    },
    props: route => ({
      presetSupportTypeId: route.query.supportType ?? null,
      presetTrackingNumber: route.query.trackingNumber ?? null,
    }),
  },
  {
    name: SUPPORT_TICKET_DETAILS_ROUTE,
    path: '/support/ticket/:ticketType/:id',
    beforeEnter: isEmailVerified,
    props: route => ({
      ticketType: route.params.ticketType,
      id: Number.parseInt(route.params.id),
    }),
    component: () => import('@/app/support/views/AppSupportTicketDetailsView.vue'),
    meta: {
      pageTitle: () => i18n.t('Support'),
      permissions: ['manage_tickets'],
      buttons: [btnListTickets, btnHelpCenter],
      showUserId: true,
    },
  },
  {
    name: SUPPORT_REFUND_CLAIMS,
    path: '/support/refund-claim/:action(accept|wait)',
    beforeEnter: isEmailVerified,
    props: route => ({
      action: route.params.action,
      payload: route.query.payload,
    }),
    component: () => import('@/app/support/views/RefundClaim.vue'),
    meta: {
      pageTitle: () => i18n.t('Support'),
      permissions: ['manage_tickets'],
      buttons: [btnListTickets, btnHelpCenter],
      showUserId: true,
    },
  },
  {
    name: SUPPORT_CLAIM,
    path: '/support/claim/manage/:payload',
    beforeEnter: isEmailVerified,
    props: route => ({
      payload: route.params.payload,
    }),
    component: () => import('@/app/support/views/GenericRefundClaim.vue'),
    meta: {
      pageTitle: () => i18n.t('Support'),
      permissions: ['manage_tickets'],
      buttons: [btnListTickets, btnHelpCenter],
      showUserId: true,
    },
  },
]
